import { useState, useRef, useCallback } from "react";
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';

const R = 110;
const FACE = 88;
const INNER = 65;

function getAngle(cx, cy) {
  const angle = Math.atan2(cy, cx) + Math.PI / 2;
  return ((angle % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
}

function pad(n) {
  return String(n).padStart(2, "0");
}

function polarToCart(angleDeg, r) {
  const a = (angleDeg * Math.PI) / 180 - Math.PI / 2;
  return { x: R + Math.cos(a) * r, y: R + Math.sin(a) * r };
}

// value is "HH:MM" in 24h format
export default function TimePicker({ value, onChange }) {
  const parse = (v) => {
    if (!v) return { h: 0, m: 0 };
    const m = v.match(/^(\d{1,2}):(\d{2})$/);
    if (m) return { h: parseInt(m[1]) || 0, m: parseInt(m[2]) || 0 };
    return { h: 0, m: 0 };
  };

  const init = parse(value);
  const [hours, setHours] = useState(init.h); // 0–23
  const [minutes, setMinutes] = useState(init.m);
  const [mode, setMode] = useState("h");
  const [inputVal, setInputVal] = useState(value);
  const svgRef = useRef(null);
  const isDragging = useRef(false);
  const [showClock, setShowClock] = useState(false);

  const notify = useCallback(
    (h, m) => {
      onChange?.(`${pad(h)}:${pad(m)}`);
    },
    [onChange]
  );

  const handleClockInteraction = useCallback(
    (e) => {
      const rect = svgRef.current.getBoundingClientRect();
      const scale = 220 / rect.width;
      const cx = (e.clientX - rect.left) * scale - R;
      const cy = (e.clientY - rect.top) * scale - R;
      const norm = getAngle(cx, cy);
      if (mode === "h") {
        const dist = Math.sqrt(cx * cx + cy * cy);
        let h = Math.round((norm / (2 * Math.PI)) * 12) % 12;
        // outer ring = 0–11, inner ring = 12–23
        if (dist < (INNER + FACE) / 2) {
          h = h === 0 ? 12 : h + 12;
        }
        setHours(h);
        setInputVal(`${pad(h)}:${pad(minutes)}`);
        notify(h, minutes);
      } else {
        let m = Math.round((norm / (2 * Math.PI)) * 60) % 60;
        setMinutes(m);
        setInputVal(`${pad(hours)}:${pad(m)}`);
        notify(hours, m);
      }
    },
    [mode, hours, minutes, notify]
  );

  const handleSvgMouseDown = (e) => { isDragging.current = true; handleClockInteraction(e); };
  const handleSvgMouseMove = (e) => { if (!isDragging.current) return; handleClockInteraction(e); };
  const handleSvgMouseUp = () => { isDragging.current = false; if (mode === "h") setMode("m"); };

  const handleInputChange = (e) => {
    const v = e.target.value;
    setInputVal(v);
    const m = v.match(/^(\d{1,2}):(\d{2})$/);
    if (m) {
      const h = parseInt(m[1]);
      const min = parseInt(m[2]);
      if (h >= 0 && h <= 23 && min >= 0 && min <= 59) {
        setHours(h); setMinutes(min);
        notify(h, min);
      }
    }
  };

  const toggleClock = () => setShowClock((v) => !v);

  // Outer ring: 0–11
  const outerMarkers = Array.from({ length: 12 }, (_, i) => {
    const val = i;
    const a = (i / 12) * 360;
    const pos = polarToCart(a, FACE);
    const isSelected = mode === "h" && hours === val;
    return { val, pos, isSelected };
  });

  // Inner ring: 12–23
  const innerMarkers = Array.from({ length: 12 }, (_, i) => {
    const val = i + 12;
    const a = (i / 12) * 360;
    const pos = polarToCart(a, INNER);
    const isSelected = mode === "h" && hours === val;
    return { val, pos, isSelected };
  });

  // Minute markers every 5 min
  const minuteMarkers = Array.from({ length: 12 }, (_, i) => {
    const val = i * 5;
    const a = (val / 60) * 360;
    const pos = polarToCart(a, FACE);
    const isSelected = mode === "m" && minutes === val;
    return { val, pos, isSelected };
  });

  // Hour hand (map 24h to 12h face)
  const hA = ((hours % 12) / 12) * 360 + (minutes / 60) * 30;
  const hPos = polarToCart(hA, hours >= 12 ? 48 : 62);

  // Minute hand
  const mA = (minutes / 60) * 360;
  const mPos = polarToCart(mA, 82);

  // Selected dot
  let selPos;
  if (mode === "h") {
    const a = ((hours % 12) / 12) * 360;
    selPos = polarToCart(a, hours >= 12 ? INNER : FACE);
  } else {
    selPos = polarToCart((minutes / 60) * 360, FACE);
  }

  return (
    <div>
      <div className="p-inputgroup flex-1">
        <InputText
          type="text"
          value={inputVal}
          onChange={handleInputChange}
          maxLength={5}
          placeholder="HH:MM"
          disabled={true}
          onClick={toggleClock}
        />
        <Button icon={showClock ? "pi pi-check" : "pi pi-pencil"} onClick={toggleClock} />
      </div>

      {showClock && <>
        <div style={styles.display}>
          <span
            style={{ ...styles.timePart, opacity: mode === "h" ? 1 : 0.45, cursor: "pointer" }}
            onClick={() => setMode("h")}
          >
            {pad(hours)}
          </span>
          <span style={styles.colon}>:</span>
          <span
            style={{ ...styles.timePart, opacity: mode === "m" ? 1 : 0.45, cursor: "pointer" }}
            onClick={() => setMode("m")}
          >
            {pad(minutes)}
          </span>
        </div>

        <div style={styles.clockWrap}>
          <svg
            ref={svgRef}
            viewBox="0 0 220 220"
            style={styles.svg}
            onMouseDown={handleSvgMouseDown}
            onMouseMove={handleSvgMouseMove}
            onMouseUp={handleSvgMouseUp}
            onMouseLeave={() => { isDragging.current = false; }}
            onTouchStart={(e) => { e.preventDefault(); handleClockInteraction(e.touches[0]); }}
            onTouchMove={(e) => { e.preventDefault(); handleClockInteraction(e.touches[0]); }}
            onTouchEnd={() => { if (mode === "h") setMode("m"); }}
          >
            <circle cx={R} cy={R} r={107} fill="none" stroke="#e0dfe0" strokeWidth="0.5" />

            <line x1={R} y1={R} x2={hPos.x} y2={hPos.y} stroke="#378ADD" strokeWidth="1.5" strokeLinecap="round" opacity="0.45" />
            <line x1={R} y1={R} x2={mPos.x} y2={mPos.y} stroke="#378ADD" strokeWidth="1.5" strokeLinecap="round" />
            <circle cx={R} cy={R} r={3} fill="#378ADD" />

            {mode === "h" && outerMarkers.map(({ val, pos, isSelected }) => (
              <g key={val} onClick={() => { setHours(val); setInputVal(`${pad(val)}:${pad(minutes)}`); notify(val, minutes); setMode("m"); }} style={{ cursor: "pointer" }}>
                {isSelected && <circle cx={pos.x} cy={pos.y} r={12} fill="#378ADD" opacity="0.2" />}
                <text x={pos.x} y={pos.y + 4} textAnchor="middle" fontSize="11" fontWeight={isSelected ? "600" : "400"} fill={isSelected ? "#185FA5" : "#888"} style={{ userSelect: "none" }}>
                  {val}
                </text>
              </g>
            ))}

            {mode === "h" && innerMarkers.map(({ val, pos, isSelected }) => (
              <g key={val} onClick={() => { setHours(val); setInputVal(`${pad(val)}:${pad(minutes)}`); notify(val, minutes); setMode("m"); }} style={{ cursor: "pointer" }}>
                {isSelected && <circle cx={pos.x} cy={pos.y} r={11} fill="#378ADD" opacity="0.2" />}
                <text x={pos.x} y={pos.y + 4} textAnchor="middle" fontSize="10" fontWeight={isSelected ? "600" : "400"} fill={isSelected ? "#185FA5" : "#aaa"} style={{ userSelect: "none" }}>
                  {val}
                </text>
              </g>
            ))}

            {mode === "m" && minuteMarkers.map(({ val, pos, isSelected }) => (
              <g key={val} onClick={() => { setMinutes(val); setInputVal(`${pad(hours)}:${pad(val)}`); notify(hours, val); }} style={{ cursor: "pointer" }}>
                {isSelected && <circle cx={pos.x} cy={pos.y} r={12} fill="#378ADD" opacity="0.2" />}
                <text x={pos.x} y={pos.y + 4} textAnchor="middle" fontSize="11" fontWeight={isSelected ? "600" : "400"} fill={isSelected ? "#185FA5" : "#888"} style={{ userSelect: "none" }}>
                  {pad(val)}
                </text>
              </g>
            ))}

            <circle cx={selPos.x} cy={selPos.y} r={11} fill="#378ADD" opacity="0.18" pointerEvents="none" />
          </svg>
        </div>

        <div style={styles.modeRow}>
          <button style={{ ...styles.modeBtn, ...(mode === "h" ? styles.modeBtnActive : {}) }} onClick={() => setMode("h")}>Horas</button>
          <button style={{ ...styles.modeBtn, ...(mode === "m" ? styles.modeBtnActive : {}) }} onClick={() => setMode("m")}>Minutos</button>
        </div>
      </>}
    </div>
  );
}

const styles = {
  display: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: 2,
    marginBottom: 14,
    marginTop: 8,
  },
  timePart: {
    fontSize: 28,
    fontWeight: 500,
    color: "#1a1a1a",
    letterSpacing: 1,
    transition: "opacity 0.15s",
  },
  colon: {
    fontSize: 28,
    fontWeight: 400,
    color: "#aaa",
    margin: "0 4px",
  },
  clockWrap: {
    display: "flex",
    justifyContent: "center",
    background: "#f7f7f8",
    borderRadius: "50%",
    width: 220,
    height: 220,
    margin: "0 auto",
    border: "0.5px solid #e0dfe0",
  },
  svg: {
    width: 220,
    height: 220,
    cursor: "pointer",
    borderRadius: "50%",
    display: "block",
  },
  modeRow: {
    display: "flex",
    justifyContent: "center",
    gap: 8,
    marginTop: 14,
  },
  modeBtn: {
    padding: "5px 20px",
    borderRadius: 8,
    border: "0.5px solid #ccc",
    background: "transparent",
    color: "#888",
    fontSize: 13,
    cursor: "pointer",
    transition: "all 0.15s",
  },
  modeBtnActive: {
    background: "#e8f1fb",
    color: "#185FA5",
    border: "0.5px solid transparent",
    fontWeight: 500,
  },
};